<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, onUnmounted, ref, watch } from 'vue'
import useUploads from '@/composables/useUploads'
import { v4 } from 'uuid'
import useAuthorization from '@/composables/useAuthorization'
import { PermissionOrganization } from '@/generated/graphql'
import { extensionToMimeType } from '@/composables/useContentTypes'

const props = defineProps<{
  mimeTypes: string[]
  disabled?: boolean
}>()
const emit = defineEmits<{
  newUploadIds: [value: string[]]
}>()

const { t } = useI18n()
const { addUploads } = useUploads()
const { hasOrgAuthz } = useAuthorization()

const accept = computed(() => {
  if (props.mimeTypes.length == 0) {
    return '*/*'
  }

  const extensions = Object.entries(extensionToMimeType)
    .filter(([, mime]) => props.mimeTypes.includes(mime))
    .map(([ext]) => `.${ext}`)

  return [...props.mimeTypes, ...extensions].join(',')
})

function enqueueUploadsFromFiles(files: File[]) {
  if (files.length === 0) {
    return
  }

  const idMap = files.reduce((map, file) => map.set(file, v4()), new Map<File, string>())
  void addUploads(files, idMap)
  emit('newUploadIds', Array.from(idMap.values()))
}

const fileInputState = ref<File[]>([])
watch(fileInputState, (v) => {
  if (!v || v.length === 0) {
    return
  }

  const files = [...v]
  // Clear immediately so selecting the same file again triggers a new upload.
  fileInputState.value = []

  enqueueUploadsFromFiles(files)
})

const fileInput = ref<HTMLInputElement>()

const classDropContainer = 'upload-drop-container'
const classDropHover = 'upload-drop-hover'
const dropFile = (event: DragEvent) => {
  if (!event.dataTransfer) {
    return
  }

  const files = [...event.dataTransfer.items]
    .filter((i) => i.kind == 'file')
    .map((i) => i.getAsFile() as File)

  Array.from(document.getElementsByClassName(classDropHover)).forEach((e) =>
    e.classList.remove(classDropHover),
  )

  enqueueUploadsFromFiles(files)
}
function dragEnter(e: DragEvent) {
  const target = e.target as HTMLElement
  if (!target.classList.contains(classDropContainer)) {
    return
  }

  target.classList.add(classDropHover)
}
function dragLeave(e: DragEvent) {
  const target = e.target as HTMLElement
  if (!target.classList.contains(classDropContainer)) {
    return
  }

  target.classList.remove(classDropHover)
}

const pasteListener = (event: ClipboardEvent) => {
  if (!event.clipboardData?.files) {
    return
  }

  enqueueUploadsFromFiles([...event.clipboardData.files])
}
window.addEventListener('paste', pasteListener)
onUnmounted(() => window.removeEventListener('paste', pasteListener))
</script>

<template>
  <v-file-input
    ref="fileInput"
    v-model="fileInputState"
    hide-details
    multiple
    :accept="accept"
    style="display: none"
    :disabled="!hasOrgAuthz(PermissionOrganization.OrgUploadOwn)"
  />

  <div
    :class="classDropContainer"
    @drop.prevent="dropFile"
    @dragover.prevent
    @dragenter.prevent="dragEnter"
    @dragleave.prevent="dragLeave"
  >
    <v-btn
      variant="tonal"
      density="comfortable"
      :icon="hasOrgAuthz(PermissionOrganization.OrgUploadOwn) ? 'cloud_upload' : 'cloud_off'"
      :disabled="!hasOrgAuthz(PermissionOrganization.OrgUploadOwn)"
      @click="fileInput?.click()"
    />
    <div class="instructions">
      {{
        t(
          `component.uploadButton.instructions${hasOrgAuthz(PermissionOrganization.OrgUploadOwn) ? 'Enabled' : 'Disabled'}`,
        )
      }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.upload-drop-container {
  margin: 0 auto;
  padding: 0.5em;
  border: 2px dashed #ccc;
  text-align: center;
}
.upload-drop-container.upload-drop-hover {
  border: 2px dashed #888;
}
.instructions {
  font-size: 0.7em;
  color: #999;
  margin-top: 0.3em;
}
</style>
